import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  selectGalleryItems,
  selectGalleryLoading,
  selectGalleryError,
  selectGalleryNextPageToken,
  selectGalleryFetched,
  useAppDispatch,
  useAppSelector,
  useSite,
  fetchGallery,
} from "../../../store";
import { useMasonry } from "../../../hooks";
import { CircleLoader, Message } from "../../components";
import { GalleryLightbox, GalleryItem } from "./";
import "./GalleryView.scss";
import { useTranslation } from "react-i18next";

const OBSERVER_ROOT_MARGIN = "200px";

export const GalleryView = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const { allowSingleImageDownload } = useSite();
  const items = useAppSelector(selectGalleryItems);
  const loading = useAppSelector(selectGalleryLoading);
  const error = useAppSelector(selectGalleryError);
  const nextPageToken = useAppSelector(selectGalleryNextPageToken);
  const fetched = useAppSelector(selectGalleryFetched);

  const [lightboxIndex, setLightboxIndex] = useState<number>(-1);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const masonryRef = useRef<HTMLDivElement | null>(null);

  const { positions, containerHeight, registerItemHeight, loadedItemCount } = useMasonry(
    masonryRef,
    items.length,
    { columnWidth: 170 }
  );

  const hasMore = useMemo(() => Boolean(nextPageToken), [nextPageToken]);

  useEffect(() => {
    const sentinel = sentinelRef.current;

    if (!sentinel || !hasMore || loadedItemCount < items.length) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !loading) {
          dispatch(fetchGallery());
        }
      });
    }, { rootMargin: OBSERVER_ROOT_MARGIN });

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [dispatch, hasMore, loading, loadedItemCount, items.length]);

  const handleItemClick = (e: React.MouseEvent, itemIndex: number) => {
    e.preventDefault();
    setLightboxIndex(itemIndex);
  };

  const handleLoadMore = useCallback(() => {
    dispatch(fetchGallery());
  }, [dispatch]);

  const getItemStyle = useCallback((index: number): React.CSSProperties => {
    const pos = positions[index];
    if (!pos) return { opacity: 0 };
    return {
      position: "absolute",
      left: pos.x,
      top: pos.y,
      width: pos.width,
    };
  }, [positions]);

  return (
    <div className="gallery-view">
      {error && <Message variant="error">{error}</Message>}

      <div
        ref={masonryRef}
        className="masonry"
        style={{ height: containerHeight }}
      >
        {items.map((item, index) => (
          <GalleryItem
            key={item.fullPath}
            downloadUrl={item.downloadUrl}
            name={item.name}
            index={index}
            downloadable={allowSingleImageDownload}
            onClick={handleItemClick}
            style={getItemStyle(index)}
            onHeightChange={registerItemHeight}
          />
        ))}
      </div>

      {loading && (
        <div>
          <CircleLoader />
        </div>
      )}

      <div ref={sentinelRef} />

      {!loading && fetched && items.length === 0 && (
        <div className="gallery-message">
          <h1>{t("gallery.emptyTitle")}</h1>
          <h3>{t("gallery.emptySubtitle")}</h3>
        </div>
      )}

      {!hasMore && !loading && items.length > 0 && (
        <div className="gallery-message">
          <h1>{t("gallery.endTitle")}</h1>
          <h3>{t("gallery.endSubtitle")}</h3>
        </div>
      )}

      <GalleryLightbox
        items={items}
        currentIndex={lightboxIndex}
        isOpen={lightboxIndex >= 0}
        hasMore={hasMore}
        loading={loading}
        onClose={() => setLightboxIndex(-1)}
        onIndexChange={setLightboxIndex}
        onLoadMore={handleLoadMore}
      />
    </div>
  );
};
